import React, { useState, useEffect, useRef } from "react";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FileActiveIcon,
  FileIcon,
  FolderIcon,
} from "./fileExplorerIcons";

function FileExplorer({
  isFileExplorerVisible,
  fileList,
  onFileClick,
  onCreateFile,
  filename,
}) {
  const [expandedFolders, setExpandedFolders] = useState([]);
  const [data, setData] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [newFilePath, setNewFilePath] = useState("");
  const inputRef = useRef(null);

  const toggleFolder = (folder) => {
    setExpandedFolders((prevExpandedFolders) => ({
      ...prevExpandedFolders,
      [folder]: !prevExpandedFolders[folder],
    }));
  };

  // Organize files into a hierarchical structure
  function createFileTree(files) {
    const fileTree = {};

    files.forEach((file) => {
      const pathSegments = file.Key.split("/");
      let currentLevel = fileTree;

      pathSegments.forEach((segment, index) => {
        if (!currentLevel[segment]) {
          if (index === pathSegments.length - 1) {
            currentLevel[segment] = { isFile: true, Key: file.Key };
          } else {
            currentLevel[segment] = {};
          }
        }
        currentLevel = currentLevel[segment];
      });
    });

    return fileTree[""];
  }

  const handleFileClick = (Key) => {
    onFileClick(Key);
  };

  const handleCreateFileClick = () => {
    setIsCreating(true);
    setNewFilePath("");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleCreateSubmit = async () => {
    if (newFilePath.trim() && onCreateFile) {
      await onCreateFile(newFilePath.trim());
    }
    setIsCreating(false);
    setNewFilePath("");
  };

  const handleCreateCancel = () => {
    setIsCreating(false);
    setNewFilePath("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleCreateSubmit();
    } else if (e.key === "Escape") {
      handleCreateCancel();
    }
  };

  const renderTree = (tree, path = "") => {
    const keys = Object.keys(tree);
    const folders = keys.filter((key) => !tree[key].isFile);
    const files = keys.filter((key) => tree[key].isFile);
    const orderedKeys = [...folders, ...files];

    return (
      <ul>
        {orderedKeys.map((key) => {
          const isFile = tree[key].isFile;
          const fullPath = path + "/" + key;
          const isActiveFile = filename === fullPath;
          return (
            <li key={fullPath}>
              <div className="tree-item">
                <div
                  className={`tree-item-icon ${isFile ? "file" : "folder"} ${
                    expandedFolders[fullPath] ? "open" : "closed"
                  } ${isActiveFile ? "active-file" : ""}`}
                  data-file={fullPath}
                  onClick={() => {
                    if (!isFile) {
                      toggleFolder(fullPath);
                    } else {
                      handleFileClick(fullPath);
                    }
                  }}
                >
                  {!isFile && (
                    <span className="tree-chevron" aria-hidden>
                      {expandedFolders[fullPath] ? (
                        <ChevronDownIcon />
                      ) : (
                        <ChevronRightIcon />
                      )}
                    </span>
                  )}
                  {isFile ? getFileIcon(isActiveFile) : getFolderIcon()}
                  <span className="file-key">{key}</span>
                </div>
              </div>
              {isFile || !expandedFolders[fullPath]
                ? null
                : renderTree(tree[key], fullPath)}
            </li>
          );
        })}
      </ul>
    );
  };

  const getFileIcon = (isActive) => {
    if (isActive) {
      return <FileActiveIcon />;
    }

    return <FileIcon />;
  };

  const getFolderIcon = () => <FolderIcon />;

  useEffect(() => {
    const folderStructure = createFileTree(fileList);
    setData(folderStructure);
  }, [fileList]);

  return (
    <div
      className="file-explorer pt-3"
      style={{ width: isFileExplorerVisible ? 300 : 0 }}
    >
      {onCreateFile && (
        <div style={{ padding: "0 10px 10px 10px" }}>
          {isCreating ? (
            <div style={{ display: "flex", gap: 4 }}>
              <input
                ref={inputRef}
                type="text"
                value={newFilePath}
                onChange={(e) => setNewFilePath(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleCreateCancel}
                placeholder="/about.html"
                className="file-explorer-input"
              />
            </div>
          ) : (
            <button
              onClick={handleCreateFileClick}
              className="nav-pill-btn file-explorer-new-btn"
              title="Create new file"
            >
              <i className="bi bi-plus-lg"></i>
              <span>New File</span>
            </button>
          )}
        </div>
      )}
      {renderTree(data)}
    </div>
  );
}

export default FileExplorer;
